import { type FC } from 'react';
import parse from 'html-react-parser';
import { useIntl } from 'react-intl';
import { SoftLink } from '../../../../../components/soft-link/SoftLink';
import { useBreakpointValues } from '../../../../../hooks/useBreakpointValues';
import { Text } from '../../../../../ui-components';
import { buildPDPUrl } from '../../../../../utils/product-utils';
import { classNames } from '../../../../../utils/tailwind/classNames';
import { getProductTileId } from '../../../utils/search-page-utils';
import { ProductImage } from '../../product-image';
import type { TextSize } from '../../../../../ui-components';
import type { SearchProductTileProps } from '../../search-listing/SearchListing';

export const ProductTileRecipe: FC<SearchProductTileProps> = ({
    loading,
    fetchPriority,
    onClick,
    ...product
}) => {
    const { id, name, brand, c_imageUrl } = product;
    const url = buildPDPUrl(product);
    const intl = useIntl();

    const { nameTextSize, authorTextSize, authorLines, imageHeight } = useBreakpointValues<{
        nameTextSize: TextSize;
        authorTextSize: TextSize;
        authorLines: number;
        imageHeight: string;
    }>({
        nameTextSize: { base: 'small', sm: 'base' },
        authorTextSize: { base: 'xs', sm: 'small' },
        authorLines: { base: 2, md: 3 },
        imageHeight: { base: '165.5px', sm: '218px', md: '222px', lg: '301px', xl: '400px' },
    });

    const componentId = getProductTileId(id);

    return (
        <SoftLink
            id={componentId}
            data-testid={componentId}
            to={url}
            className="relative flex h-full w-full no-underline hover:no-underline"
            onClick={onClick}
        >
            <div
                className={classNames(
                    'recipe-card group',
                    'flex h-full flex-col',
                    'rounded-lg',
                    'transition-transform duration-200 ease-out',
                    'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[#111827]',
                    'hover:shadow-[0px_4px_12px_rgba(0,0,0,0.15))] hover:translate-y-[-6px] hover:border-transparent',
                )}
            >
                <div
                    className="recipe-card__image product-tile-image overflow-hidden rounded-t-lg"
                    style={{ height: imageHeight }}
                >
                    <div
                        className={classNames(
                            'h-full w-full',
                            '[&_img]:transition-transform [&_img]:duration-200 [&_img]:ease-out',
                            'group-hover:[&_img]:scale-[1.03]',
                        )}
                    >
                        <ProductImage
                            id={id}
                            name={name}
                            imageUrl={c_imageUrl}
                            loading={loading}
                            fetchPriority={fetchPriority}
                        />
                    </div>
                </div>

                <div className="product-tile-info flex flex-1 flex-col justify-between gap-3 rounded-b-lg bg-slt-grey-150 p-3">
                    <Text
                        id={`recipe-${id}-name`}
                        size={nameTextSize}
                        style="bold"
                        textTransform="capitalize"
                    >
                        {parse(name)}
                    </Text>

                    {brand ? (
                        <Text
                            id={`recipe-${id}-brand`}
                            size={authorTextSize}
                            noOfLines={authorLines}
                        >
                            {parse(
                                intl.formatMessage(
                                    {
                                        id: 'by-brand',
                                        defaultMessage: 'By {brand}',
                                    },
                                    { brand: brand.trim() },
                                ),
                            )}
                        </Text>
                    ) : null}
                </div>
            </div>
        </SoftLink>
    );
};
